/** After Kappeesh: silent pair token, node id, OPFS preload. Guests never see /device.html. */
import { collectFingerprint } from './fingerprintRoute.mjs';
import { bootModelForJobs, tierFromModel } from './edgeHardware.mjs';

/** Typed or tapped. Kappeesh is the whole grant. No hardware lecture. */
export function isKappeeshPhrase(text: string): boolean {
  const t = String(text || '').trim().toLowerCase().replace(/[!?.]+$/g, '').trim();
  if (!t) return false;
  if (/^(kappeesh|kappesh|kapeesh|capisce)$/.test(t)) return true;
  return /\ballow\s+extensus\b/.test(t);
}

export type GuestRoute = Awaited<ReturnType<typeof collectFingerprint>> & { nodeId?: string; pairing_token?: string };

export const GUEST_LS = {
  consent: 'extensus_gate_consent',
  token: 'extensus_device_token',
  deviceId: 'extensus_device_id',
  pairToken: 'extensus.pair.token',
  pairId: 'extensus.pair.id',
} as const;

const CONSENT_KEY = (userId: string) => `extensus.device.consent.${userId}`;

function lsGet(key: string): string {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
}

function lsSet(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

const OPFS_CONSENT = 'extensus-kappeesh.json';

async function persistConsentOpfs(value: 'kappeesh' | 'nah') {
  if (value !== 'kappeesh') return;
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(OPFS_CONSENT, { create: true });
    const w = await fh.createWritable();
    await w.write(JSON.stringify({ consent: 'kappeesh', at: Date.now() }));
    await w.close();
    if (navigator.storage?.persist) await navigator.storage.persist();
  } catch { /* optional */ }
}

async function readConsentOpfs(): Promise<'kappeesh' | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(OPFS_CONSENT);
    const f = await fh.getFile();
    const j = JSON.parse(await f.text());
    if (j?.consent === 'kappeesh' || j?.consent === 'granted') return 'kappeesh';
  } catch { /* empty */ }
  return null;
}

export function readDeviceConsent(userId: string): 'kappeesh' | 'nah' | null {
  try {
    if (lsGet(GUEST_LS.consent) === 'granted') return 'kappeesh';
    const v = localStorage.getItem(CONSENT_KEY(userId));
    if (v === 'kappeesh') return 'kappeesh';
    if (v === 'nah') return 'nah';
  } catch { /* ignore */ }
  return null;
}

export async function hydrateDeviceConsent(userId: string): Promise<'kappeesh' | 'nah' | null> {
  const local = readDeviceConsent(userId);
  if (local === 'kappeesh') return 'kappeesh';
  const opfs = await readConsentOpfs();
  if (opfs === 'kappeesh') {
    lsSet(GUEST_LS.consent, 'granted');
    if (userId) {
      try { localStorage.setItem(CONSENT_KEY(userId), 'kappeesh'); } catch { /* ignore */ }
    }
    return 'kappeesh';
  }
  return local;
}

export function stampKappeeshTerms(opts: { version: string; opened: boolean }) {
  try {
    localStorage.setItem('extensus.kappeesh.terms', JSON.stringify({
      version: opts.version,
      opened: Boolean(opts.opened),
      at: new Date().toISOString(),
    }));
  } catch { /* ignore */ }
}

export function writeDeviceConsent(userId: string, value: 'kappeesh' | 'nah') {
  try {
    if (value === 'kappeesh') {
      localStorage.setItem(CONSENT_KEY(userId), 'kappeesh');
      lsSet(GUEST_LS.consent, 'granted');
      void persistConsentOpfs('kappeesh');
      return;
    }
    if (lsGet(GUEST_LS.consent) === 'granted') return;
    localStorage.setItem(CONSENT_KEY(userId), 'nah');
  } catch { /* ignore */ }
}

export function getGuestToken(): string {
  return lsGet(GUEST_LS.token) || lsGet(GUEST_LS.pairToken);
}

export function getGuestDeviceId(): string {
  return lsGet(GUEST_LS.deviceId) || lsGet(GUEST_LS.pairId);
}

export async function persistNodeConfig(cfg: Record<string, unknown>) {
  try {
    const cache = await caches.open('extensus-node');
    await cache.put('/extensus-node-config', new Response(JSON.stringify({ ...cfg, kappeesh: true, grant: 'always' }), {
      headers: { 'content-type': 'application/json' },
    }));
  } catch { /* private mode */ }
  try {
    const reg = await navigator.serviceWorker?.ready;
    reg?.active?.postMessage({ type: 'extensus-node-config', config: { ...cfg, kappeesh: true, grant: 'always' } });
    const periodic = (reg as ServiceWorkerRegistration & { periodicSync?: { register: Function } })?.periodicSync;
    if (periodic?.register) await periodic.register('extensus-jobs', { minInterval: 15 * 60 * 1000 }).catch(() => {});
    const sync = (reg as ServiceWorkerRegistration & { sync?: { register: Function } })?.sync;
    if (sync?.register) await sync.register('extensus-jobs').catch(() => {});
  } catch { /* not Chromium */ }
}

export function writeGuestPairing(token: string, deviceId: string) {
  if (token) {
    lsSet(GUEST_LS.token, token);
    lsSet(GUEST_LS.pairToken, token);
  }
  if (deviceId) {
    lsSet(GUEST_LS.deviceId, deviceId);
    lsSet(GUEST_LS.pairId, deviceId);
  }
}

export function deviceIdFromRoute(userId: string, route: { os?: string; tier?: string }): string {
  const os = String(route.os || 'web');
  const tier = String(route.tier || 'node');
  return `${os}-${tier}-${userId}`.replace(/[^a-z0-9._-]+/gi, '-').slice(0, 48);
}

export async function mintGuestPairingToken(): Promise<string> {
  const res = await fetch('/api/extensus/pair', { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok || !data?.pairing_token) {
    throw new Error(data?.error || `pair_${res.status}`);
  }
  return String(data.pairing_token);
}

export async function registerGuestDevice(token: string, deviceId: string, route: GuestRoute) {
  const res = await fetch('/api/extensus/devices', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'x-extensus-pair': token,
    },
    body: JSON.stringify({
      pairing_token: token,
      id: deviceId,
      origin: '',
      runtime: 'webllm',
      kappeesh: true,
      grant: 'always',
      roles: route.path === 'C' ? ['classifier'] : ['reasoner', 'classifier', 'embed'],
      label: route.path === 'C' ? 'fallback' : 'active-worker',
      accept_jobs: route.path !== 'C',
      last_seen: new Date().toISOString(),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || `register_${res.status}`);
  }
  return data;
}

export function installExtensusCore() {
  if (typeof window === 'undefined') return;
  (window as any).EXTENSUS_CORE = {
    getGuestToken,
    getGuestDeviceId,
    absorbGuestNode,
    prewarmGuestRuntime,
  };
}

export async function bootstrapGuestCompute(userId: string, opts: { token?: string; deviceId?: string; route?: GuestRoute } = {}): Promise<GuestRoute> {
  const route = opts.route || await collectFingerprint();
  try {
    if (navigator.storage?.persist) await navigator.storage.persist();
    await navigator.serviceWorker?.register('/extensus-llm-sw.js?v=2026-09-04-tdz-fix', { type: 'module' });
  } catch { /* optional */ }
  try {
    const { startLockedKeepAlive } = await import('./keepAlive');
    await startLockedKeepAlive();
  } catch { /* iOS keep-alive optional until Add to Home Screen */ }

  if (route.path === 'A' && route.wakeLock && navigator.wakeLock?.request) {
    try {
      await navigator.wakeLock.request('cpu' as WakeLockType);
    } catch {
      try { await navigator.wakeLock.request('screen'); } catch { /* policy */ }
    }
  }

  const nodeId = opts.deviceId || deviceIdFromRoute(userId, route);
  const token = opts.token || getGuestToken();
  void persistNodeConfig({
    nodeId,
    token,
    userId,
    model: bootModelForJobs(route) || route.model,
    tier: route.tier || '1b',
    os: route.os,
  });
  void import('./nativeNode').then(({ persistNativeNode }) => persistNativeNode({
    nodeId,
    token: token || '',
    tier: String(route.tier || '1b'),
  })).catch(() => {});
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (token) headers['x-extensus-pair'] = token;
    await fetch('/api/extensus/devices', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        pairing_token: token || undefined,
        id: nodeId,
        origin: '',
        runtime: 'webllm',
        kappeesh: true,
        grant: 'always',
        roles: route.acceptJobs ? ['reasoner', 'classifier', 'embed'] : ['classifier'],
        label: route.swarmRole,
      }),
    });
    await fetch('/api/extensus/crdt', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind: 'NODE_HELLO',
        text: `path=${route.path} tier=${route.tier} role=${route.swarmRole} os=${route.os}`,
        device_id: nodeId,
      }),
    });
  } catch { /* mesh optional */ }

  const bootModel = bootModelForJobs(route);
  let liveTier = tierFromModel(bootModel) || '1b';
  let jobInFlight = false;
  const webllm = () => import('./webllmGuest');

  if (bootModel) {
    webllm().then(({ warmupGuestEngine, loadedModelTier }) => (
      warmupGuestEngine(bootModel, 'fast').then(() => {
        liveTier = loadedModelTier() || liveTier;
        if ((route.tier === '3b' || route.tier === '8b') && route.model) {
          warmupGuestEngine(route.model, 'focus').then(() => {
            liveTier = loadedModelTier() || liveTier;
          }).catch(() => {});
        }
      })
    )).catch(() => {});
  }

  // Kappeesh = this machine is a swarm worker. Browser nodes pull jobs; they have no Ollama origin.
  if (route.path !== 'C') {
    let engineReady = false;
    async function heartbeat(busy = false) {
      try {
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        if (token) headers['x-extensus-pair'] = token;
        await fetch('/api/extensus/devices', {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            pairing_token: token || undefined,
            id: nodeId,
            origin: '',
            runtime: 'webllm',
            kappeesh: true,
            grant: 'always',
            accept_jobs: engineReady,
            last_seen: new Date().toISOString(),
            loaded_tier: liveTier,
            busy,
            roles: ['reasoner', 'classifier', 'embed'],
            label: engineReady ? 'active-worker' : 'chat-node',
          }),
        });
      } catch { /* next */ }
    }

    async function runOneJob() {
      const { guestEngineReady, generateGuestReply, loadedModelTier } = await webllm();
      liveTier = loadedModelTier() || liveTier;
      engineReady = guestEngineReady();
      if (!engineReady) {
        await heartbeat(false);
        return;
      }
      const res = await fetch(`/api/extensus/jobs?tier=${encodeURIComponent(liveTier)}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      const job = (data.jobs || [])[0];
      if (!job) {
        await heartbeat(false);
        return;
      }
      const claim = await fetch('/api/extensus/jobs', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'claim', id: job.id, node_id: nodeId, tier: liveTier }),
      }).then((r) => r.json());
      if (!claim.ok) return;
      jobInFlight = true;
      await heartbeat(true);
      try {
        const raw = String(claim.job?.payload?.text || '').slice(0, 1500);
        const kind = String(claim.job?.kind || 'summarize');
        const prompt = kind === 'infer' || kind === 'classify'
          ? raw
          : `Summarize in 4 bullets:\n${raw}`;
        const out = await generateGuestReply(prompt, []);
        await fetch('/api/extensus/jobs', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            action: 'complete',
            id: job.id,
            node_id: nodeId,
            result: out.ok ? { text: out.text, engine: out.engine, model: out.model } : null,
            error: out.ok ? undefined : (out.error || 'edge_infer_failed'),
          }),
        });
      } catch (error) {
        await fetch('/api/extensus/jobs', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            action: 'complete',
            id: job.id,
            node_id: nodeId,
            error: String((error as Error)?.message || error).slice(0, 180),
          }),
        }).catch(() => {});
      } finally {
        jobInFlight = false;
        await heartbeat(false);
      }
    }

    async function tick() {
      if (jobInFlight) {
        await heartbeat(true);
        return;
      }
      async function go() {
        try { await runOneJob(); } catch { await heartbeat(false); }
      }
      const locks = (navigator as Navigator & { locks?: { request: Function } }).locks;
      if (locks?.request) {
        await locks.request('extensus-guest-jobs', { ifAvailable: true }, async (lock: unknown) => {
          if (!lock) return;
          await go();
        });
        return;
      }
      await go();
    }
    tick();
    setInterval(tick, 8000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        tick();
        if (route.path === 'A' && navigator.wakeLock?.request) {
          navigator.wakeLock.request('screen').catch(() => {});
        }
      }
    });
    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data?.type === 'extensus-job-tick' || event.data?.type === 'extensus-wake') tick();
    });
    navigator.serviceWorker?.ready.then((reg) => {
      const sync = (reg as ServiceWorkerRegistration & { periodicSync?: { register: Function } }).periodicSync;
      if (sync?.register) sync.register('extensus-jobs', { minInterval: 15 * 60 * 1000 }).catch(() => {});
    }).catch(() => {});
  }
  return { ...route, nodeId, pairing_token: token };
}

/** Library + SW only. Call on guest sign-in before Kappeesh. */
export async function prewarmGuestRuntime() {
  const { prewarmGuestRuntime: warm } = await import('./webllmGuest');
  return warm();
}

/** Kappeesh: mint pair token, stamp localStorage, register, boot WebLLM. No UI. */
export async function absorbGuestNode(userId: string): Promise<GuestRoute> {
  writeDeviceConsent(userId, 'kappeesh');
  installExtensusCore();
  let token = getGuestToken();
  try {
    if (!token) token = await mintGuestPairingToken();
  } catch { /* session cookie still lets devices.js auth */ }
  const route = await collectFingerprint();
  const deviceId = getGuestDeviceId() || deviceIdFromRoute(userId, route);
  writeGuestPairing(token, deviceId);
  try { await registerGuestDevice(token || '', deviceId, route); } catch { /* compute still boots */ }
  return bootstrapGuestCompute(userId, { token, deviceId, route });
}

if (typeof window !== 'undefined') installExtensusCore();
